export interface ChatSource {
  sourceSystem: string;
  sourceUrl: string;
  entityId?: string;
  entityName?: string;
}

export interface ChatMessage {
  role: 'User' | 'Assistant';
  content: string;
  sources: ChatSource[];
  timestamp: Date;
  /** True while the assistant message is still being built via SSE streaming. */
  isStreaming?: boolean;
  /** Navigation links rendered as buttons below the message content. */
  actionLinks?: { label: string; route: string }[];
  /** Background job status displayed inline below the message that queued it. */
  jobProgress?: AssessmentProgressDto;
}

export interface ChatSession {
  id: string;
  title?: string;
  createdAt: Date;
  lastMessageAt: Date;
  messageCount: number;
}

export interface ChatRequest {
  query: string;
  sessionId: string;
  knowledgeSources?: string[];
  /** Present only when user is on an application detail view. Scope is resolved by the backend. */
  trackedBusinessApplicationId?: number;
}

export interface ChatResponse {
  message: string;
  sources: ChatSource[];
  sessionId: string;
  timestamp: Date;
}

export interface ChatStreamChunk {
  content: string;
  isComplete: boolean;
  sessionId: string;
  timestamp: Date;
  sources?: ChatSource[];
}

export interface AiSettings {
  id: number;
  endpoint: string;
  modelName: string;
  deployment: string;
  apiVersion: string;
  maxCompletionTokens: number;
  maxContextWindowTokens: number;
  cmdbTokenBudget: number;
  historyTokenBudget: number;
  summaryTokenBudget: number;
  systemPrompt: string;
  isActive: boolean;
}

export enum AiFeature {
  AIChat = 0,
  AIAssessments = 1
}

export interface AiConnection {
  id: number;
  name: string;
  endpoint: string;
  modelName: string;
  deployment: string;
  apiVersion: string;
  /** New in migration: optional API mode. Backend expects exactly 'ChatCompletions'|'Responses' when present. */
  apiMode?: 'ChatCompletions' | 'Responses';
  /** New in migration: nullable reasoning effort string (e.g. 'low','medium','high','xhigh','none' or null for auto). */
  reasoningEffort?: string | null;
  maxCompletionTokens: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiChatSettings {
  id: number;
  aiConnectionId: number;
  isActive: boolean;
  maxContextWindowTokens?: number;
  cmdbTokenBudget?: number;
  historyTokenBudget?: number;
  summaryTokenBudget?: number;
  systemPrompt?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiChatSettingsDetail {
  id: number;
  isActive: boolean;
  connection: AiConnection;
  maxContextWindowTokens?: number;
  cmdbTokenBudget?: number;
  historyTokenBudget?: number;
  summaryTokenBudget?: number;
  systemPrompt?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiAssessmentSettings {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  aiConnectionId: number;
  keyVaultSecretNameForPat: string;
  azureDevOpsBaseUrl: string;
  azureDevOpsOrganization?: string;
  azureDevOpsProject?: string;
  azureDevOpsWorkItemTitlePrefix?: string;
  azureDevOpsParentWorkItemId?: number;
  pollingIntervalSeconds: number;
  maxFileSizeBytes: number;
  maxContentCharsPerDimensionChunk: number;
  cicdPrompt: string;
  authPrompt: string;
  codePrompt: string;
  dependenciesPrompt: string;
  hostingPrompt: string;
  apiPrompt: string;
  containerPrompt: string;
  testingPrompt: string;
  productionPrompt: string;
  databasePrompt: string;
  masterPrompt: string;
  additionalDetailsPrompt: string;
  lightweightCodeValidationPrompt: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiAssessmentSettingsDetail {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  connection: AiConnection;
  keyVaultSecretNameForPat: string;
  azureDevOpsBaseUrl: string;
  azureDevOpsOrganization?: string;
  azureDevOpsProject?: string;
  azureDevOpsWorkItemTitlePrefix?: string;
  azureDevOpsParentWorkItemId?: number;
  pollingIntervalSeconds: number;
  maxFileSizeBytes: number;
  maxContentCharsPerDimensionChunk: number;
  cicdPrompt: string;
  authPrompt: string;
  codePrompt: string;
  dependenciesPrompt: string;
  hostingPrompt: string;
  apiPrompt: string;
  containerPrompt: string;
  testingPrompt: string;
  productionPrompt: string;
  databasePrompt: string;
  masterPrompt: string;
  additionalDetailsPrompt: string;
  lightweightCodeValidationPrompt: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiAssessmentSettingsVersionSummary {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  createdByUserId?: string | null;
  createdAt: Date;
}

// Modernization Readiness Settings
export interface AiModernizationReadinessSettings {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  aiConnectionId: number;
  prompt: string;
  deprecatedTechnologies?: string | null;
  apiDelayMs: number;
  batchSize: number;
  maxRetriesPerApplication: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiModernizationReadinessSettingsDetail {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  connection: AiConnection;
  prompt: string;
  deprecatedTechnologies?: string | null;
  apiDelayMs: number;
  batchSize: number;
  maxRetriesPerApplication: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiModernizationReadinessSettingsVersionSummary {
  id: number;
  version: number;
  versionName?: string | null;
  isActive: boolean;
  createdByUserId?: string | null;
  createdAt: Date;
}

export enum ModernizationReadinessJobStatus {
  Pending = 0,
  Running = 1,
  Completed = 2,
  Failed = 3,
  Cancelled = 4
}

export interface ModernizationReadinessJob {
  id: number;
  status: ModernizationReadinessJobStatus | string;
  totalApplications: number;
  processedCount: number;
  successCount: number;
  failedCount: number;
  skippedCount: number;
  currentBatch: number;
  totalBatches: number;
  currentApplicationSysId?: string | null;
  currentApplicationName?: string | null;
  createdAt: Date;
  startedAt?: Date | null;
  completedAt?: Date | null;
  triggeredByUserId?: string | null;
  triggeredByUserName?: string | null;
  settingsVersionUsed: number;
  errorMessage?: string | null;
  lastCheckpointSysId?: string | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
}

export interface ModernizationReadinessJobLog {
  id: number;
  jobId: number;
  businessApplicationSysId: string;
  applicationName?: string | null;
  success: boolean;
  skipped: boolean;
  skipReason?: string | null;
  errorMessage?: string | null;
  readinessScore?: number | null;
  recommendedAction?: string | null;
  processingTimeMs?: number | null;
  retryCount: number;
  aiResponseJson?: string | null;
  processedAt: Date;
  promptTokens: number;
  completionTokens: number;
}

export interface ModernizationReadinessCalculationStatus {
  isRunning: boolean;
  isPaused: boolean;
  currentJob?: ModernizationReadinessJob | null;
  queueLength: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  currentPhase?: string | null;
}

export interface TriggerCalculationResponse {
  success: boolean;
  message: string;
  jobId?: number;
}

export interface LiveLogEntry {
  id: number;
  timestamp: string;
  level: string;
  category: string;
  message: string;
}

export interface LiveLogResponse {
  entries: LiveLogEntry[];
  latestId: number;
}

// Legacy interface for backward compatibility
export interface AiFeatureSettings extends AiChatSettings {
  feature?: AiFeature;
}

export interface AiFeatureSettingsDetail extends AiChatSettingsDetail {
  feature?: AiFeature;
}

// ─── AI Code Assessment Admin ──────────────────────────────────────────────────

export enum AssessmentJobStatus {
  Pending = 0,
  Running = 1,
  Completed = 2,
  Failed = 3
}

export enum AssessmentJobType {
  FullCodeAssessment = 0,
  LightweightCodeValidation = 1
}

export interface AssessmentJob {
  id: number;
  status: AssessmentJobStatus | string;
  jobType?: AssessmentJobType | string;
  repositoryUrl: string;
  branch: string;
  orionId?: string | null;
  trackedBusinessApplicationId?: number | null;
  settingsId: number;
  createdAt: Date;
  startedAt?: Date | null;
  completedAt?: Date | null;
  errorMessage?: string | null;
  requestedByEmail?: string | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalScore?: number | null;
  /** When GitHub rate-limited this job: deadline string (UTC Z) or null. Kept for historical reasons. */
  rateLimitedUntil?: Date | string | null;
  /** Number of retry attempts queued by the server due to GitHub delays. May be >0 even when rateLimitedUntil is null. */
  rateLimitRetryCount?: number;
  lightweightResult?: LightweightCodeValidationResult | null;
}

export interface LightweightCodeValidationResult {
  repositoryUrl?: string | null;
  branch?: string | null;
  codebaseSize: LightweightCodebaseSize;
  technologyStack: string[];
  legacyIndicators: LightweightCodeIndicator[];
  modernIndicators: LightweightCodeIndicator[];
  deploymentCharacteristics: string[];
  pipelineMaturity: string;
  testCoverageIndicators: string[];
  cmdbComparison: string;
  recommendedAction: string;
  message?: string | null;
  validatedAt: Date | string;
}

export interface LightweightCodebaseSize {
  totalFiles: number;
  manifestFiles: number;
  projectFiles: number;
  solutionFiles: number;
}

export interface LightweightCodeIndicator {
  signal: string;
  filePath: string;
  severity: string;
  explanation: string;
}

export interface AssessmentJobHistoryPaged {
  jobs: AssessmentJob[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Live progress snapshot for an assessment job started from Nora chat. */
export interface AssessmentProgressDto {
  jobId: number;
  jobType: 'CodeAssessment' | 'Readiness';
  status: string;
  isActive: boolean;
  errorMessage?: string | null;
  totalScore?: number | null;
  recentLogLines: string[];
  latestLogId: number;
  /** DB ID of the linked application (if available). Used to build the detail link. */
  trackedBusinessApplicationId?: number | null;
  /** Orion ID of the linked application. */
  orionId?: string | null;
  applicationName?: string | null;
  readinessScore?: number | null;
  readinessOutcome?: string | null;
  readinessRecommendation?: string | null;
  readinessKeyReason?: string | null;
}
